import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConflictDetectionModal = ({ conflicts = [], onResolve, onClose }) => {
  if (conflicts?.length === 0) {
    return null;
  }

  const handleKeepLocal = (conflict) => {
    onResolve?.(conflict, 'keep_local');
  };

  const handleKeepRemote = (conflict) => {
    onResolve?.(conflict, 'keep_remote');
  };

  const handleMerge = (conflict) => {
    onResolve?.(conflict, 'merge');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" className="w-5 h-5 text-orange-500" />
            <h3 className="text-lg font-semibold text-gray-900">Editing Conflicts Detected</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="X" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-sm text-gray-600 mb-4">
            {conflicts?.length} conflict{conflicts?.length > 1 ? 's' : ''} detected. 
            Another user has modified the same content. Choose how to resolve each conflict.
          </p>

          <div className="space-y-4">
            {conflicts?.map((conflict, index) => (
              <div key={index} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-medium text-gray-900">
                      {conflict?.type === 'node_conflict' ? 'Node Conflict' : 'Connection Conflict'}
                    </div>
                    <div className="text-sm text-gray-600">
                      Modified by: <span className="font-medium">{conflict?.remoteUser}</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(conflict?.timestamp)?.toLocaleTimeString()}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1">Your Changes</div>
                    <div className="text-sm text-gray-700">
                      {conflict?.localChange?.changeType}
                    </div>
                  </div>
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1">Remote Changes</div>
                    <div className="text-sm text-gray-700">
                      {conflict?.remoteChange?.change_type}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleKeepLocal(conflict)}
                    className="flex-1"
                  >
                    Keep Mine
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleKeepRemote(conflict)}
                    className="flex-1"
                  >
                    Keep Theirs
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleMerge(conflict)}
                    className="flex-1"
                  >
                    Merge Both
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConflictDetectionModal;